import { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOptionChainAction } from '../../../../../store/actions/watchlist';
import octopusInstance from '../../../../../services/octopus/octopusInstance';
import { initializeDynamicRows } from '../../../../Overview/OptionDataUtils';

/**
 * Hook to manage Option Chain data for MCX Commodities.
 * Centered on the LTP of the nearest future contract.
 */
export const useCommodityOptionChain = (selectedCommodity, activeFuture, liveSpotPrice) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [resolvedToken, setResolvedToken] = useState(null);
  const [selectedExpiry, setSelectedExpiry] = useState(null);

  // Get option chain data from Redux
  const rawOptionChain = useSelector(state => state.marketwatch.optionChainData);
  const [filteredChain, setFilteredChain] = useState([]);
  const [availExpiries, setAvailExpiries] = useState([]);

  // Track handlers for cleanup
  const handlersRef = useRef([]);

  useEffect(() => {
    setLoading(true); // Start spinner immediately
    setResolvedToken(null); // Clear resolved token for new future
    setFilteredChain([]);
    setSelectedExpiry(null);
    setAvailExpiries([]);
    dispatch({ type: 'SET_OPTION_CHAIN_DATA', payload: [] });
  }, [selectedCommodity.key, activeFuture?.token, dispatch]);

  const spotPriceRef = useRef(0);
  const fetchTimeoutRef = useRef(null);
  const hasFetchedForCurrentToken = useRef(false);
  const lastFetchedToken = useRef(null);

  // 2. Fetch Option Chain logic
  const fetchChain = useCallback(() => {
    if (!activeFuture?.token) return;

    setLoading(true);
    const payload = {
      exchange: 'MCX',
      token: activeFuture.token,
      num: 10,
      price: spotPriceRef.current || activeFuture.ltp || activeFuture.close_price || 0,
      instrument_name: activeFuture.name || selectedCommodity.searchQuery
    };

    dispatch(fetchOptionChainAction(payload))
      .catch((err) => {
        dispatch({ type: 'SET_OPTION_CHAIN_DATA', payload: [] });
      })
      .finally(() => {
        setLoading(false);
        setResolvedToken(activeFuture.token);
      });
  }, [selectedCommodity, activeFuture?.token, activeFuture?.name, activeFuture?.ltp, activeFuture?.close_price, dispatch]);

  // Synchronize Ref with live LTP from the spotlight (activeFuture or passed prop)
  useEffect(() => {
    const currentLtp = liveSpotPrice || activeFuture?.ltp;
    if (currentLtp) {
      spotPriceRef.current = currentLtp;
      
      // If we are waiting for the first tick to fetch, do it now
      if (!hasFetchedForCurrentToken.current && activeFuture?.token === lastFetchedToken.current) {
        fetchChain();
        hasFetchedForCurrentToken.current = true;
        if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
      }
    }
  }, [liveSpotPrice, activeFuture?.ltp, activeFuture?.token, fetchChain]);

  // Trigger fetch when token changes
  useEffect(() => {
    if (activeFuture?.token && lastFetchedToken.current !== activeFuture.token) {
      lastFetchedToken.current = activeFuture.token;
      hasFetchedForCurrentToken.current = false;
      spotPriceRef.current = liveSpotPrice || activeFuture.ltp || 0;
      
      // If we already have a price, fetch immediately
      if (spotPriceRef.current > 0) {
        fetchChain();
        hasFetchedForCurrentToken.current = true;
      } else {
        // Otherwise wait for tick or fallback
        if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = setTimeout(() => {
          if (!hasFetchedForCurrentToken.current) {
            fetchChain();
            hasFetchedForCurrentToken.current = true;
          }
        }, 500);
      }
    }

    return () => {
      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    };
  }, [activeFuture?.token, activeFuture?.ltp, fetchChain]);

  // Process raw data from Redux
  useEffect(() => {
    if (rawOptionChain && rawOptionChain.length > 0) {
      // Helper to parse "16JUN26" to a timestamp for sorting
      const parseExpiry = (dateStr) => {
        if (!dateStr || dateStr.length < 7) return Infinity;
        const day = dateStr.substring(0, 2);
        const month = dateStr.substring(2, 5);
        const year = "20" + dateStr.substring(5, 7);
        const timestamp = new Date(`${day} ${month} ${year}`).getTime();
        return isNaN(timestamp) ? Infinity : timestamp;
      };

      const todayMidnight = new Date().setHours(0,0,0,0);

      // Filter out dummy expiries and PAST expiries
      const validGroups = rawOptionChain.filter(item => {
        if (!item.strikes || item.strikes.length === 0) return false;
        const expiryTime = parseExpiry(item.expiry_date);
        return expiryTime >= todayMidnight;
      });
      
      const uniqueExpiries = [...new Set(validGroups.map(item => item.expiry_date))];
      // Sort expiries chronologically so the nearest (latest) is first
      const expiries = uniqueExpiries.sort((a, b) => parseExpiry(a) - parseExpiry(b));

      setAvailExpiries(expiries);

      // Select valid expiry: if none selected OR currently selected is not in the list, default to first
      let currentExpiry = selectedExpiry;
      if (!currentExpiry || !expiries.includes(currentExpiry)) {
        if (expiries.length > 0) {
          currentExpiry = expiries[0];
          setSelectedExpiry(currentExpiry);
        }
      }

      const selectedExpiryGroup = rawOptionChain.find(item => item.expiry_date === currentExpiry);
      const strikes = selectedExpiryGroup?.strikes || [];

      const processedStrikes = initializeDynamicRows(strikes, 'MCX');

      setFilteredChain(prevChain => {
        if (prevChain.length === 0) return processedStrikes;

        return processedStrikes.map(newRow => {
          const existing = prevChain.find(p => p.strike_price === newRow.strike_price);
          if (existing) {
            return {
              ...newRow,
              ce_ltp: (existing.ce_ltp > 0) ? existing.ce_ltp : (newRow.ce_ltp || 0),
              ce_change: (existing.ce_ltp > 0) ? existing.ce_change : (newRow.ce_change || 0),
              ce_abs_change: (existing.ce_ltp > 0) ? existing.ce_abs_change : (newRow.ce_abs_change || 0),
              pe_ltp: (existing.pe_ltp > 0) ? existing.pe_ltp : (newRow.pe_ltp || 0),
              pe_change: (existing.pe_ltp > 0) ? existing.pe_change : (newRow.pe_change || 0),
              pe_abs_change: (existing.pe_ltp > 0) ? existing.pe_abs_change : (newRow.pe_abs_change || 0),
              ce_oi: existing.ce_oi || newRow.ce_oi,
              pe_oi: existing.pe_oi || newRow.pe_oi,
              ce_volume: existing.ce_volume || newRow.ce_volume,
              pe_volume: existing.pe_volume || newRow.pe_volume
            };
          }
          return newRow;
        });
      });
    } else {
      // Explicitly clear data if Redux data is empty (handles API failure/cleanup)
      setFilteredChain([]);
      setAvailExpiries([]);
    }
  }, [rawOptionChain, selectedExpiry]);

  // WebSocket Live Updates (Exchange Code 4 for MCX)
  useEffect(() => {
    if (filteredChain.length === 0) return;

    // Cleanup previous handlers
    handlersRef.current.forEach(h => h.unsubscribe());
    handlersRef.current = [];

    const strikeSubscriptions = filteredChain.flatMap(row => [
      { token: row.ce_token, key: `ce-${row.ce_token}` },
      { token: row.pe_token, key: `pe-${row.pe_token}` }
    ]).filter(item => item.token);

    handlersRef.current = strikeSubscriptions.map(item => {
      const handler = octopusInstance.wsHandler({
        messageType: 'CompactMarketDataMessage',
        subscriptionLocation: `commodity-opt-strike-${item.token}`,
        payload: {
          exchangeCode: 4, // MCX = 4
          instrumentToken: Number(item.token),
        },
      });

      if (handler) {
        handler.subscribe(({ msg }) => {
          setFilteredChain(prev => prev.map(row => {
            const updateRow = (rowToken, prefix) => {
              if (rowToken === item.token) {
                const ltp = msg.ltp || msg.lastTradedPrice || msg.Price || row[`${prefix}_ltp`] || 0;
                const close = row[`${prefix}_close`] || 0;
                const absChange = msg.absoluteChange ?? (close > 0 ? (ltp - close) : (row[`${prefix}_abs_change`] || 0));
                const pctChange = msg.percentChange ?? (close > 0 ? ((ltp - close) / close) * 100 : (row[`${prefix}_change`] || 0));
                return {
                  ...row,
                  [`${prefix}_ltp`]: ltp,
                  [`${prefix}_change`]: pctChange,
                  [`${prefix}_abs_change`]: absChange,
                  [`${prefix}_oi`]: msg.openInterest || msg.OI || row[`${prefix}_oi`] || 0,
                  [`${prefix}_volume`]: msg.volume || msg.Volume || row[`${prefix}_volume`] || 0
                };
              }
              return null;
            };

            const ceUpdate = updateRow(row.ce_token, 'ce');
            if (ceUpdate) return ceUpdate;

            const peUpdate = updateRow(row.pe_token, 'pe');
            if (peUpdate) return peUpdate;

            return row;
          }));
        });
      }

      return handler;
    }).filter(Boolean);

    return () => {
      handlersRef.current.forEach(h => h.unsubscribe());
      handlersRef.current = [];
    };
  }, [JSON.stringify(filteredChain.map(r => `${r.ce_token}-${r.pe_token}`)), selectedExpiry]); // Re-subscribe if tokens actually change

  return {
    loading,
    resolvedToken,
    optionChain: filteredChain,
    expiries: availExpiries,
    selectedExpiry,
    setSelectedExpiry,
    refresh: fetchChain
  };
};
